import { useState, useEffect, useContext, useMemo } from "react";
import api from '../api/axios'; 
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { PageContainer, PageTitleBadge, DataGridTable, StatCard, SearchBar, FilterSelect, FilterDateGroup, FiltersContainer } from "../components";
import { FaPlus, FaCalendarAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "react-toastify/dist/ReactToastify.css";
import "../styles/common.css";
import "./Evenements.css";

export default function Evenements() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animaux, setAnimaux] = useState([]);
  const [filtres, setFiltres] = useState({
    type_evenement: '',
    date_debut: '',
    date_fin: '',
    animal: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('evenements-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('evenements-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchEvenements = async () => {
      setLoading(true);
      setError(null);
      try {
        const animauxResponse = await api.get('/api/animaux/');
        setAnimaux(animauxResponse.data);
        const params = new URLSearchParams();
        if (filtres.type_evenement) params.append('type_evenement', filtres.type_evenement);
        if (filtres.date_debut) params.append('date_after', filtres.date_debut);
        if (filtres.date_fin) params.append('date_before', filtres.date_fin);
        if (filtres.animal) params.append('animal', filtres.animal);
        if (searchTerm) params.append('search', searchTerm);

        const response = await api.get('/api/evenements/', { params });
        setEvenements(response.data);
      } catch (err) {
        setError("Erreur lors du chargement des événements.");
      } finally {
        setLoading(false);
      }
    };
    fetchEvenements();
  }, [filtres, searchTerm]);

  const handleFiltreChange = (e) => {
    setFiltres({
      ...filtres,
      [e.target.name]: e.target.value,
    });
  };


  // Calculer les statistiques avec useMemo pour garantir la mise à jour
  // Exclure les événements d'annulation (type "annulation" ou ceux qui contiennent "-> actif" dans les détails)
  const stats = useMemo(() => {
    // Fonction pour détecter si un événement est une annulation
    const isAnnulation = (event) => {
      return event.type_evenement === "annulation" || 
             (event.details && event.details.includes("-> actif"));
    };
    
    // Filtrer les événements pour exclure les annulations
    const evenementsSansAnnulation = evenements.filter(e => !isAnnulation(e));
    
    return {
      naissances: evenementsSansAnnulation.filter(e => e.type_evenement === "naissance").length,
      achats: evenementsSansAnnulation.filter(e => e.type_evenement === "achat").length,
      ventes: evenementsSansAnnulation.filter(e => e.type_evenement === "vendu").length,
      mortalites: evenementsSansAnnulation.filter(e => e.type_evenement === "mort" || e.type_evenement === "deces").length,
      zakat: evenementsSansAnnulation.filter(e => e.type_evenement === "zakat").length,
    };
  }, [evenements]);

  // Fonction pour annuler un événement (mort ou vente)
  const handleAnnuler = async (eventId, animalId, eventType) => {
    const eventLabel = eventType === "vendu" ? "vente" : "décès";
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler ce ${eventLabel} ? L'animal redeviendra actif.`)) {
      return;
    }

    // Cache the original event payload for rollback
    let originalEventPayload = null;

    try {
      // Step 1: Fetch and cache the original event before deletion
      try {
        const eventResponse = await api.get(`/api/evenements/${eventId}/`);
        originalEventPayload = {
          animal: eventResponse.data.animal,
          type_evenement: eventResponse.data.type_evenement,
          date: eventResponse.data.date,
          details: eventResponse.data.details || "",
        };
      } catch (fetchError) {
        console.error("Erreur lors de la récupération de l'événement:", fetchError);
        toast.error("Impossible de récupérer les données de l'événement. Annulation impossible.");
        return;
      }

      // Step 2: Delete the event
      await api.delete(`/api/evenements/${eventId}/`);
      
      // Step 3: Remettre l'animal à "actif" with rollback on failure
      if (animalId) {
        try {
          const animalData = { statut: "actif" };
          await api.patch(`/api/animaux/${animalId}/`, animalData);
          
          // Step 4: Créer un événement d'annulation avec un type distinct
          const annulationDetails = `Annulation de l'événement ${eventType} (ID: ${eventId}). Statut de l'animal modifié : ${eventType === "vendu" ? "vendu" : "mort"} -> actif`;
          const annulationEvent = {
            animal: animalId,
            type_evenement: "annulation", // Type distinct pour éviter la contamination des stats
            date: new Date().toISOString().split('T')[0], // Date du jour
            details: annulationDetails
          };
          
          try {
            await api.post(`/api/evenements/`, annulationEvent);
          } catch (eventError) {
            console.error("Erreur lors de la création de l'événement d'annulation:", eventError);
            // Ne pas bloquer si l'événement d'annulation ne peut pas être créé
            toast.warning("L'annulation a été effectuée mais l'événement de traçage n'a pas pu être créé.");
          }
        } catch (animalError) {
          console.error("Erreur lors de la mise à jour du statut de l'animal:", animalError);
          
          // Rollback: Re-create the original event
          if (originalEventPayload) {
            try {
              await api.post(`/api/evenements/`, originalEventPayload);
              toast.error("Erreur lors de la mise à jour du statut de l'animal. L'événement a été restauré pour maintenir la cohérence des données.");
            } catch (rollbackError) {
              console.error("Erreur critique lors de la restauration de l'événement:", rollbackError);
              toast.error("Erreur critique : L'événement a été supprimé mais n'a pas pu être restauré. Veuillez contacter l'administrateur.");
            }
          } else {
            toast.error("Erreur lors de la mise à jour du statut de l'animal. L'événement a été supprimé mais n'a pas pu être restauré.");
          }
          return;
        }
      }
      
      toast.success(`${eventType === "vendu" ? "Vente" : "Décès"} annulé avec succès. L'animal est redevenu actif.`);
      
      // Recharger les événements
      const params = new URLSearchParams();
      if (filtres.type_evenement) params.append('type_evenement', filtres.type_evenement);
      if (filtres.date_debut) params.append('date_after', filtres.date_debut);
      if (filtres.date_fin) params.append('date_before', filtres.date_fin);
      if (filtres.animal) params.append('animal', filtres.animal);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get('/api/evenements/', { params });
      setEvenements(response.data);
    } catch (error) {
      console.error("Erreur lors de l'annulation:", error);
      
      // If we deleted the event but haven't completed the process, try to restore it
      if (originalEventPayload) {
        try {
          await api.post(`/api/evenements/`, originalEventPayload);
          toast.error("Erreur lors de l'annulation. L'événement a été restauré pour maintenir la cohérence des données.");
        } catch (rollbackError) {
          console.error("Erreur critique lors de la restauration de l'événement:", rollbackError);
          const errorMessage = error.response?.data?.detail || 
                              error.response?.data?.message ||
                              "Une erreur s'est produite lors de l'annulation et la restauration a échoué. Veuillez contacter l'administrateur.";
          toast.error(errorMessage);
        }
      } else {
        const errorMessage = error.response?.data?.detail || 
                            error.response?.data?.message ||
                            "Une erreur s'est produite lors de l'annulation.";
        toast.error(errorMessage);
      }
    }
  };

  // Préparer les données pour DataGrid et trier par date décroissante puis par ID décroissant
  const rows = useMemo(() => {
    const mappedEvents = evenements.map(evenement => {
      // Créer un timestamp plus précis en combinant date et ID pour le tri
      const dateStr = evenement.date || '';
      const timestamp = dateStr ? new Date(dateStr).getTime() : 0;
      
      return {
        id: evenement.id,
        animal: evenement.matricule_animal || "N/A",
        animal_id: evenement.animal, // ID de l'animal pour l'annulation
        type_evenement: evenement.type_evenement,
        date: evenement.date,
        details: evenement.details || "Aucun détail",
        isAutoEvent: evenement.type_evenement === "annulation" || (evenement.details && evenement.details.includes("Statut de l'animal modifié")), // Détecter les événements automatiques
        timestamp: timestamp, // Pour le tri
      };
    });
    
    // Trier par date décroissante puis par ID décroissant pour garantir l'ordre
    // Cela garantit que le dernier événement créé apparaît toujours en premier
    return mappedEvents.sort((a, b) => {
      // D'abord trier par date (décroissant) - les dates les plus récentes en premier
      if (b.timestamp !== a.timestamp) {
        return b.timestamp - a.timestamp;
      }
      // Si même date, trier par ID décroissant (l'ID le plus élevé = le plus récent)
      return b.id - a.id;
    });
  }, [evenements]);

  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'animal',
      headerName: 'Animal',
      width: 150,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'type_evenement',
      headerName: 'Type d\'événement',
      width: 180,
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const type = params.value;
        const typeLabels = {
          'naissance': 'Naissance',
          'achat': 'Achat',
          'vendu': 'Vente',
          'mort': 'Mortalité',
          'deces': 'Mortalité', // Support pour les anciennes données
          'zakat': 'Zakat',
          'annulation': 'Annulation'
        };
        return typeLabels[type] || type;
      },
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 130,
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'details',
      headerName: 'Détails',
      width: 250,
      flex: 1,
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
    },
  ], []);

  const adminColumns = useMemo(() => {
    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_EVENEMENT);
    if (!canEdit) return [];
    
    return [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          const eventType = params.row.type_evenement;
          const isMortOrVente = eventType === "mort" || eventType === "vendu";
          const isAutoEvent = params.row.isAutoEvent; // Événement automatique (changement de statut)
          
          // Ne pas permettre d'annuler les événements automatiques
          if (isMortOrVente && !isAutoEvent) {
            return (
              <button
                onClick={() => handleAnnuler(params.row.id, params.row.animal_id, eventType)}
                className="grid-action-button grid-action-button-danger"
                style={{ fontSize: `${1.3 * zoomLevel}rem` }}
                title="Annuler cet événement"
              >
                Annuler
              </button>
            );
          }
          
          // Les événements automatiques n'ont pas de bouton d'action
          if (isAutoEvent) {
            return <span style={{ color: '#999', fontSize: `${1.3 * zoomLevel}rem` }}>-</span>;
          }
          
          // Pour les autres événements, afficher le bouton Modifier
          return (
            <button
              onClick={() => navigate(`/evenements/${params.row.id}/modifier`)}
              className="grid-action-button"
              style={{ fontSize: `${1.3 * zoomLevel}rem` }}
            >
              Modifier
            </button>
          );
        },
      },
    ];
  }, [user, navigate, zoomLevel]);

  const columns = useMemo(() => {
    return adminColumns.length > 0 ? [...baseColumns, ...adminColumns] : baseColumns;
  }, [baseColumns, adminColumns]);

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">Chargement des événements...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title={t.error} icon={<FaCalendarAlt />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={t.eventList} icon={<FaCalendarAlt />} />

      {/* Bouton d'action admin */}
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_EVENEMENT)) && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-evenement")}
          >
            <FaPlus />
            <span>{t.addEvent}</span>
          </button>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder={t.searchByEvent}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
        />
      </div>

      {/* Filtres */}
      <FiltersContainer title={t.filters}>
        <FilterSelect
          name="type_evenement"
          value={filtres.type_evenement}
          onChange={handleFiltreChange}
          options={[
            { value: "naissance", label: t.birth },
            { value: "mort", label: t.mortality },
            { value: "vendu", label: t.sale },
            { value: "achat", label: t.purchase },
            { value: "zakat", label: t.zakat }
          ]}
          placeholder={t.filterByEventType}
          className="filtre-type"
          width="220px"
        />
        <FilterSelect
          name="animal"
          value={filtres.animal}
          onChange={handleFiltreChange}
          options={animaux.map(animal => ({ id: animal.id, nom: animal.matricule }))}
          placeholder={t.filterByAnimal}
          className="filtre-animal"
          width="200px"
        />
        <FilterDateGroup
          label={t.startDate}
          name="date_debut"
          value={filtres.date_debut}
          onChange={handleFiltreChange}
        />
        <FilterDateGroup
          label={t.endDate}
          name="date_fin"
          value={filtres.date_fin}
          onChange={handleFiltreChange}
        />
      </FiltersContainer>

      {/* Statistiques */}
      <div className="stats-container">
        <StatCard type="naissance" label="Naissances" value={stats.naissances} variant="default" />
        <StatCard type="achat" label="Achats" value={stats.achats} variant="default" />
        <StatCard type="vente" label="Ventes" value={stats.ventes} variant="default" />
        <StatCard type="mortalite" label="Mortalités" value={stats.mortalites} variant="default" />
        <StatCard type="zakat" label="Zakat" value={stats.zakat} variant="default" />
      </div>

      {/* DataGrid */}
      {evenements.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">Aucun événement n'a été enregistré pour l'instant.</p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="evenements-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'date', sort: 'desc' }}
          pageSize={25}
          containerClassName="evenements-grid-container"
          initialState={{
            sorting: { 
              sortModel: [
                { field: 'date', sort: 'desc' },
                { field: 'id', sort: 'desc' }
              ] 
            }
          }}
        />
      )}
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </PageContainer>
  );
}
